/**
 * Формула игрового индекса «EW3-v1» (публичная, п. 4.2 ТЗ).
 *
 *   I(t) = I₀ · (1 + (1/3) · Σᵢ (pᵢ(t) / Bᵢ − 1))
 *
 *   Bᵢ  — цена закрытия акции i на баре P₀ (общая база в момент начала розыгрыша);
 *   I₀  = 5000,00 пунктов — значение индекса на закрытии бара P₀, т.е. P₀ = I₀ всегда;
 *   шаг цены 0,25 пункта; вычисления в двойной точности IEEE-754;
 *   округление до ближайшего шага, половина шага — вверх.
 *
 * OHLC бара агрегата:
 *   O = I(открытия), C = I(закрытия);
 *   H = max(I(максимумы), O, C), L = min(I(минимумы), O, C) — верхняя/нижняя граница,
 *   т.к. максимумы компонентов могли достигаться в разные моменты минуты.
 *
 * Пропуски: если у акции нет бара в минуте (не было сделок), используется последняя
 * известная цена закрытия (O = H = L = C = предыдущее закрытие, объём 0).
 *
 * Активность (вместо объёма): v = (1/3) · Σᵢ volᵢ(t) / avgVolᵢ, где avgVolᵢ — средний объём
 * акции по окну истории розыгрыша. Безразмерная величина (1 = обычная активность);
 * абсолютные объёмы отдельных акций не раскрываются.
 */
/** Минутный бар одной акции, время — начало минуты (UTC, мс). */
export interface MinuteBar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export const MINUTE = 60_000;

export const FORMULA_ID = 'EW3-v1';
export const INDEX_BASE = 5000;
export const TICK = 0.25;

export function roundToTick(x: number, tick = TICK): number {
  const ticks = Math.floor(x / tick + 0.5 + 1e-9);
  return Math.round(ticks * tick * 100) / 100;
}

export function indexValue(prices: number[], bases: number[], base = INDEX_BASE): number {
  let sum = 0;
  for (let i = 0; i < prices.length; i++) sum += prices[i]! / bases[i]! - 1;
  return base * (1 + sum / prices.length);
}

export interface AggregateBar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export function aggregateBar(t: number, comps: MinuteBar[], bases: number[], avgVol: number[]): AggregateBar {
  const o = roundToTick(indexValue(comps.map((b) => b.o), bases));
  const c = roundToTick(indexValue(comps.map((b) => b.c), bases));
  const h = Math.max(roundToTick(indexValue(comps.map((b) => b.h), bases)), o, c);
  const l = Math.min(roundToTick(indexValue(comps.map((b) => b.l), bases)), o, c);
  let act = 0;
  for (let i = 0; i < comps.length; i++) act += comps[i]!.v / Math.max(1, avgVol[i]!);
  const v = Math.round((act / comps.length) * 100) / 100;
  return { t, o, h, l, c, v };
}
